import { injectable } from "inversify";
import ShiftRepository from "../../../domain/repositories/ShiftRepository";
import { Either, right, left } from "fp-ts/lib/Either";
import ErrorEntity from "../../../domain/entities/ErrorEntity";
import ShiftEntity, { ShiftStatus } from "../../../domain/entities/ShiftEntity";
import UserEntity, { UserType } from "../../../domain/entities/UserEntity";
import StablishmentEntity from "../../../domain/entities/StablishmentEntity";
import Testing from "../../../presentation/utils/Testing";
import SearchResultEntity from "../../../domain/entities/SearchResultEntity";
import ScheduleEntity, { ScheduleDay } from "../../../domain/entities/ScheduleEntity";

const fakeShift: ShiftEntity = new ShiftEntity({
    turnId: "turn-123",
    id: "123",
    status: ShiftStatus.PENDING,
    startDate: new Date("2024-10-23T09:00:00"),
    endDate: new Date("2024-10-23T17:00:00"),
    changes: [
        {
            date: new Date("2024-10-23T09:00:00"),
            action: "sendedShiftNotification"
        },
        {
            date: new Date("2024-10-23T17:00:00"),
            action: "acceptedByUser"
        }
    ],
    stablishment: {
        id: "1",
        name: "Test Stablishment",
        address: "123 Test Street"
    } as StablishmentEntity,
    user: {
        id: "1",
        name: "Test User",
        role: UserType.VALET
    } as UserEntity,
    schedule:
        new ScheduleEntity({ id: "sch-1", day: ScheduleDay.lunes, openTime: "09:00", closeTime: "12:00", }),

});

@injectable()
class ShiftRepositoryFake implements ShiftRepository {

    async searchShifts({ query, page, itemsPerPage }: import("../../../domain/repositories/ShiftRepository").SearchShiftsParams): Promise<Either<ErrorEntity, SearchResultEntity<ShiftEntity>>> {
        await Testing.sleeper(1000);
        const fakeChanges = (index: number) => {
            const mod = index % 3;
            if (mod === 0) {
                // Sin historial
                return [];
            } else if (mod === 1) {
                // Con historial, sin userName
                return [
                    { date: new Date("2024-10-23T09:00:00"), action: "sendedShiftNotification" },
                    { date: new Date("2024-10-23T17:00:00"), action: "acceptedByUser" },
                ];
            } else {
                // Con historial, con userName en algunos
                return [
                    { date: new Date("2024-10-23T09:00:00"), action: "sendedShiftNotification" },
                    { date: new Date("2024-10-23T12:00:00"), action: "reasignedByAdmin", userName: "Admin Lopez" },
                    { date: new Date("2024-10-23T17:00:00"), action: "acceptedByUser" },
                    { date: new Date("2024-10-23T18:00:00"), action: "rejectedBySupervisor", userName: "Supervisor Garcia" },
                ];
            }
        };

        const fakeResults: ShiftEntity[] = Array.from({ length: itemsPerPage }, (_, index) =>
            new ShiftEntity({
                turnId: `turn-${page * itemsPerPage + index + 1}`,
                id: (page * itemsPerPage + index + 1).toString(),
                status: Object.values(ShiftStatus)[index % Object.values(ShiftStatus).length],
                startDate: new Date(Date.now() + index * 24 * 60 * 60 * 1000),
                endDate: new Date(Date.now() + (index * 24 + 8) * 60 * 60 * 1000),
                changes: fakeChanges(index),
                stablishment: {
                    id: (index + 1).toString(),
                    name: `Stablishment ${index + 1}`,
                    address: `${100 + index} Test Street`
                } as StablishmentEntity,
                user: {
                    id: ((index % 3) + 1).toString(),
                    name: `User ${(index % 3) + 1}`,
                    role: Object.values(UserType)[index % Object.values(UserType).length]
                } as UserEntity,
                schedule: new ScheduleEntity({ id: "sch-1", day: ScheduleDay.lunes, openTime: "09:00", closeTime: "12:00", }),

            })
        );

        return right({
            data: fakeResults,
            total: 100,
            page,
            itemsPerPage,
            pages: Math.ceil(100 / itemsPerPage)
        });
    }

    async getShiftById(id: string): Promise<Either<ErrorEntity, ShiftEntity>> {
        await Testing.sleeper(1000);
        if (id === "not-found") {
            return left({ code: "NotFound" });
        }
        return right(new ShiftEntity({
            ...fakeShift,
            id
        }));
    }

    async deleteShift(id: string): Promise<Either<ErrorEntity, void>> {
        await Testing.sleeper(1000);
        if (id === "cannot-delete") {
            return left({ code: "CannotDelete" });
        }
        return right(undefined);
    }

    async updateShift(shift: ShiftEntity): Promise<Either<ErrorEntity, ShiftEntity>> {
        await Testing.sleeper(1000);
        return right(shift);
    }

    async createShiftWithSchedule(_: { scheduleId: string, date: Date, userId: string }): Promise<Either<ErrorEntity, ShiftEntity>> {
        await Testing.sleeper(1000);

        const newShift = new ShiftEntity({
            id: `shift-${Date.now()}`,
            status: ShiftStatus.PENDING,
            startDate: _.date,
            endDate: new Date(_.date.getTime() + 8 * 60 * 60 * 1000), // assuming 8 hours shift
            stablishment: fakeShift.stablishment,
            turnId: "turn-123",
            user: {
                id: _.userId,
                name: "Nuevo Usuario",
                role: UserType.VALET
            } as UserEntity,
            changes: [
                {
                    date: new Date(_.date.getTime() + 8 * 60 * 60 * 1000),
                    action: "sendedShiftNotification"
                },
                {
                    date: new Date(_.date.getTime() + 16 * 60 * 60 * 1000),
                    action: "acceptedByUser"
                }
            ], schedule: new ScheduleEntity({
                id: _.scheduleId,
                day: ScheduleDay.lunes,
                openTime: "09:00",
                closeTime: "17:00"
            })
        });

        return right(newShift);
    }

    async reasignShift(_: {
        shiftId: string, newUserId: string, oldUserId: string, reason: string
    }): Promise<Either<ErrorEntity, ShiftEntity>> {
        await Testing.sleeper(1000);

        if (_.shiftId === "cannot-reasign") {
            return left({ code: "CannotReasign" });
        }

        const reasignedShift = new ShiftEntity({
            ...fakeShift,
            id: _.shiftId,
            user: {
                id: _.newUserId,
                name: "Usuario Reasignado",
                role: UserType.VALET,
                email: "reasignado@test.com"
            } as UserEntity,
            status: ShiftStatus.PENDING // Reset status to pending after reasignment
        });

        return right(reasignedShift);
    }
}

export default ShiftRepositoryFake;